import {
    Chip,
    ClickAwayListener,
    Collapse,
    Divider,
    Grow,
    IconButton,
    InputAdornment,
    List,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import {
    Add,
    Checklist,
    Close,
    FilterList,
    Search,
    VisibilityOffOutlined,
    VisibilityOutlined,
} from '@mui/icons-material';
import {
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { DetailsCardHint } from '~/components/DetailsCard';
import SectionTitle from '~/components/SectionTitle';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { RoiId } from '~/domain/changes/ids';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import {
    RoiReadModel,
    StructureSetReadModel,
} from '~/domain/readmodels/StructureSetReadModel';
import { useRoiListView } from '~/hooks/domain/useRoiListView';
import { useStructureSetListView } from '~/hooks/domain/useStructureSetListView';
import { useClearMultiSelection } from '~/hooks/structure-set/useClearMultiSelection';
import useStructureSet from '~/hooks/structure-set/useStructureSet';
import {
    autoShownRoiNumberState,
    bulkRoiSelectionActiveState,
    multiSelectedRoiNumbersState,
    multiSelectModeState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import BulkActionsPanel from './BulkActionsPanel';
import {
    applyCtrlToggle,
    applyModeToggle,
    applyShiftRange,
    pruneSelection,
    reconcileActiveRoi,
} from './multiSelect';
import { getTypeSortGroup, sortRois } from './roiSort';
import StructureLayer from './StructureList/Structure';
import StructureDetails from './StructureList/Structure/StructureProperties/StructureDetails/StructureDetails';
import { ICON_BUTTON_PX, ICON_PX } from '~/theme/iconSizes';

/** Height of the structure search field, in CSS pixels. */
const SEARCH_FIELD_PX = 32;
/** The add action leads the section, so its glyph is larger than the others. */
const ADD_ICON_PX = 20;

interface StructureSetProps {
    structureSet: StructureSetReadModel;
    renderExtraDetails?: (
        roi: RoiReadModel,
        roiId: RoiId | undefined
    ) => ReactNode;
    readOnly?: boolean;
    /**
     * The list only shows and hides structures: it edits, deletes and
     * multi-selects nothing, and has no details card, which leaves the room
     * to a step that shows structures only to look at them.
     */
    visibilityOnly?: boolean;
    /**
     * What the details card says while the set has structures but none is
     * selected. Without it, the card is left out until one is.
     */
    selectHint?: ReactNode;
}

/**
 * The structure list of one structure set. Renders nothing while there is
 * no patient session: the Recoil structure set can arrive one commit before
 * PatientSessionProvider's effect creates the session, and the list's
 * projection and command hooks require one.
 */
export default function StructureSet(props: StructureSetProps) {
    const session = useSessionOrNull();
    if (!session) {
        return null;
    }
    return <StructureSetContent {...props} />;
}

function StructureSetContent({
    structureSet,
    renderExtraDetails,
    readOnly = false,
    visibilityOnly = false,
    selectHint,
}: StructureSetProps) {
    const intl = useIntl();
    const itemRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const listRef = useRef<HTMLUListElement | null>(null);
    const selectedROINumber = useRecoilValue(selectedROIState);
    const setSelectedROINumber = useSetRecoilState(selectedROIState);
    const setAutoShown = useSetRecoilState(autoShownRoiNumberState);
    const [multiSelected, setMultiSelected] = useRecoilState(
        multiSelectedRoiNumbersState
    );
    // Last non-shift click; the origin for a shift-range. Cleared on set change.
    const anchorRef = useRef<number | null>(null);
    const { createRoi } = useStructureSet();

    // Projection hooks — resolve the branded StructureSetId by matching dicomUid,
    // then get the list view. These run unconditionally to satisfy hook rules.
    const structureSetItems = useStructureSetListView();
    const listItem = useMemo(
        () =>
            structureSetItems.find(
                (i) => i.dicomUid === structureSet.dicomUid
            ) ?? null,
        [structureSetItems, structureSet.dicomUid]
    );
    const roiListView = useRoiListView(listItem?.id);

    /**
     * Map from roiNumber → RoiId (from the projection). Used to pass branded
     * RoiId to row components so they can call useRoiCommands(). roiNumber is
     * the only ROI identifier DICOM requires to be unique within a structure
     * set — ROIName is free text and imported sets can carry duplicates, so
     * keying by name would route commands to the wrong structure.
     */
    const roiIdByNumber = useMemo((): ReadonlyMap<number, RoiId> => {
        const map = new Map<number, RoiId>();
        for (const item of roiListView) {
            map.set(item.roiNumber, item.id);
        }
        return map;
    }, [roiListView]);
    const [createMenuOpen, setCreateMenuOpen] = useState(false);
    const createButtonRef = useRef<HTMLDivElement>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTypes, setSelectedTypes] = useState<Set<RoiInterpretedType>>(
        new Set()
    );
    const [filterPanelOpen, setFilterPanelOpen] = useState(false);

    const handleCloseCreateMenu = (event: Event) => {
        if (
            createButtonRef.current &&
            createButtonRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }
        setCreateMenuOpen(false);
    };

    const selectedStructure = structureSet?.roiArray?.find(
        (roi) => roi?.roiNumber === selectedROINumber
    );

    const sortedRois = useMemo(() => {
        if (!structureSet?.roiArray) {
            return [];
        }
        return [...structureSet.roiArray].sort(sortRois);
    }, [structureSet]);

    const presentTypes = useMemo(() => {
        const types = new Set<RoiInterpretedType>();
        for (const roi of sortedRois) {
            if (roi.interpretedType) {
                types.add(roi.interpretedType);
            }
        }
        return Array.from(types).sort(
            (a, b) => getTypeSortGroup(a) - getTypeSortGroup(b)
        );
    }, [sortedRois]);

    // Remove stale type filters when structures are deleted
    useEffect(() => {
        setSelectedTypes((prev) => {
            const presentSet = new Set(presentTypes);
            const stale = [...prev].some((t) => !presentSet.has(t));
            if (!stale) {
                return prev;
            }
            const next = new Set([...prev].filter((t) => presentSet.has(t)));
            return next;
        });
    }, [presentTypes]);

    const filteredRois = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        return sortedRois.filter((roi) => {
            if (query && !roi.name.toLowerCase().includes(query)) {
                return false;
            }
            if (
                selectedTypes.size > 0 &&
                !selectedTypes.has(roi.interpretedType)
            ) {
                return false;
            }
            return true;
        });
    }, [sortedRois, searchQuery, selectedTypes]);

    /**
     * Multi-select mode: a plain row click toggles membership, and every
     * row shows a checkbox. The button arms it; a ctrl/cmd or shift click
     * that selects a second structure arms it too, so a selection made
     * either way reads and behaves the same.
     */
    const [multiSelectMode, setMultiSelectMode] =
        useRecoilState(multiSelectModeState);
    const clearSelectionAndMode = useClearMultiSelection();
    const clearMultiSelection = useCallback(() => {
        clearSelectionAndMode();
        anchorRef.current = null;
    }, [clearSelectionAndMode]);

    // Clear bulk selection on structure-set switch: ROI numbers are reused
    // across sets, so read-time pruning alone could rebind a stale entry.
    useEffect(() => {
        clearMultiSelection();
    }, [structureSet.dicomUid, clearMultiSelection]);

    // Drop selected members that no longer exist; a selection pruned to
    // nothing ends the mode with it.
    useEffect(() => {
        const existing = new Set(
            structureSet.roiArray.map((roi) => roi.roiNumber)
        );
        const pruned = pruneSelection(multiSelected, existing);
        if (pruned === multiSelected) {
            return;
        }
        if (pruned.size === 0) {
            clearMultiSelection();
            return;
        }
        setMultiSelected(pruned);
    }, [structureSet, multiSelected, setMultiSelected, clearMultiSelection]);

    // The control reports the same state the rest of the app gates on,
    // rather than a second definition of "multi-select is on" that has to
    // be kept in step with it.
    const multiSelectActive = useRecoilValue(bulkRoiSelectionActiveState);
    const toggleMultiSelectMode = useCallback(() => {
        if (multiSelectActive) {
            clearMultiSelection();
            return;
        }
        setMultiSelectMode(true);
    }, [multiSelectActive, clearMultiSelection, setMultiSelectMode]);

    // Keep the active ROI a member of a non-empty bulk set so its row highlight
    // can't disagree with the selection chips. A direct atom write moves only
    // the highlight — no auto-show or toggle-off, matching how members are
    // otherwise selected in bulk mode.
    const reconcileActiveSelection = useCallback(
        (nextSet: ReadonlySet<number>, clicked: number) => {
            const order = filteredRois.map((roi) => roi.roiNumber);
            const target = reconcileActiveRoi(
                nextSet,
                clicked,
                selectedROINumber,
                order
            );
            if (target !== selectedROINumber) {
                setSelectedROINumber(target);
                setAutoShown(null);
            }
        },
        [filteredRois, selectedROINumber, setSelectedROINumber, setAutoShown]
    );

    const handleRowSelectionInput = useCallback(
        (roiNumber: number, mods: { ctrl: boolean; shift: boolean }) => {
            const order = filteredRois.map((roi) => roi.roiNumber);

            if (mods.shift) {
                const anchor =
                    anchorRef.current ??
                    (selectedROINumber !== -1 &&
                    order.includes(selectedROINumber)
                        ? selectedROINumber
                        : null);
                if (anchor === null) {
                    return false;
                }
                const next = applyShiftRange(order, anchor, roiNumber);
                if (next.size < 2) {
                    return false;
                }
                setMultiSelected(next);
                setMultiSelectMode(true);
                reconcileActiveSelection(next, roiNumber);
                return true;
            }

            if (multiSelectMode) {
                // In mode a click, with or without ctrl/cmd, ticks or
                // unticks one row: every size is legitimate, including the
                // one the user is passing through, so nothing collapses and
                // the active structure is not silently enrolled. Only an
                // active structure that was a member moves with the set.
                const next = applyModeToggle(multiSelected, roiNumber);
                anchorRef.current = roiNumber;
                setMultiSelected(next);
                if (multiSelected.has(selectedROINumber) && next.size > 0) {
                    reconcileActiveSelection(next, roiNumber);
                }
                return true;
            }

            anchorRef.current = roiNumber;
            if (!mods.ctrl) {
                return false;
            }
            const next = applyCtrlToggle(
                multiSelected,
                selectedROINumber,
                roiNumber
            );
            if (next.size < 2) {
                return false;
            }
            setMultiSelected(next);
            setMultiSelectMode(true);
            reconcileActiveSelection(next, roiNumber);
            return true;
        },
        [
            filteredRois,
            multiSelected,
            multiSelectMode,
            selectedROINumber,
            setMultiSelected,
            setMultiSelectMode,
            reconcileActiveSelection,
        ]
    );

    const hiddenSelectedRoiNumbers = useMemo(() => {
        const visibleNumbers = new Set(
            filteredRois.map((roi) => roi.roiNumber)
        );
        const hidden = new Set<number>();
        for (const roiNumber of multiSelected) {
            if (!visibleNumbers.has(roiNumber)) {
                hidden.add(roiNumber);
            }
        }
        return hidden;
    }, [filteredRois, multiSelected]);

    const handleRemoveFromSelection = useCallback(
        async (roiNumber: number) => {
            const next = new Set(multiSelected);
            next.delete(roiNumber);
            if (next.size >= 2) {
                setMultiSelected(next);
                // Removing the active member's chip would orphan its highlight;
                // move the active ROI to a survivor.
                reconcileActiveSelection(next, roiNumber);
                return;
            }
            clearMultiSelection();
            // Hand the survivor back to single-selection; re-selecting the
            // active ROI would toggle it off, so skip when already active.
            const survivor: number | undefined = next.values().next().value;
            if (survivor !== undefined && survivor !== selectedROINumber) {
                await domainEventBus.publish(
                    new RoiSelectionRequestedEvent(survivor, 'user')
                );
            }
        },
        [
            multiSelected,
            selectedROINumber,
            setMultiSelected,
            reconcileActiveSelection,
            clearMultiSelection,
        ]
    );

    const allVisible = useMemo(
        () =>
            filteredRois.length > 0 && filteredRois.every((roi) => roi.visible),
        [filteredRois]
    );

    const handleToggleAllVisibility = useCallback(async () => {
        const newVisibility = !allVisible;
        // Hide-all skips the selected ROI so focus isn't yanked away.
        const targets = newVisibility
            ? filteredRois
            : filteredRois.filter((roi) => roi.roiNumber !== selectedROINumber);
        await Promise.all(
            targets.map((roi) =>
                domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roi.roiNumber,
                        newVisibility,
                        'user'
                    )
                )
            )
        );
        // After Hide-all the selected structure is shown only for its selection.
        setAutoShown(
            !newVisibility && selectedROINumber !== -1
                ? selectedROINumber
                : null
        );
    }, [filteredRois, allVisible, selectedROINumber, setAutoShown]);

    const handleToggleType = (type: RoiInterpretedType) => {
        setSelectedTypes((prev) => {
            const next = new Set(prev);
            if (next.has(type)) {
                next.delete(type);
            } else {
                next.add(type);
            }
            return next;
        });
    };

    // A new selection is brought into view. One the search or type filter
    // hides clears the filter first, so the structure the next edit lands
    // on is always in the list; its row mounts a render later and is
    // scrolled to then. A filter typed over the selection is left alone.
    const handledSelection = useRef<number | null>(null);
    const scrollPending = useRef(false);
    useEffect(() => {
        if (handledSelection.current !== selectedROINumber) {
            handledSelection.current = selectedROINumber;
            scrollPending.current = true;
            const filteredOut =
                sortedRois.some((r) => r.roiNumber === selectedROINumber) &&
                !filteredRois.some((r) => r.roiNumber === selectedROINumber);
            if (filteredOut) {
                setSearchQuery('');
                setSelectedTypes(new Set());
            }
        }
        const item = itemRefs.current[selectedROINumber];
        const list = listRef.current;
        if (scrollPending.current && selectedROINumber && item && list) {
            scrollPending.current = false;
            const itemRect = item.getBoundingClientRect();
            const listRect = list.getBoundingClientRect();
            const isAboveView = itemRect.top < listRect.top;
            const isBelowView = itemRect.bottom > listRect.bottom;
            if (isAboveView || isBelowView) {
                item.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [selectedROINumber, sortedRois, filteredRois]);

    const assignItemRef = (roiNumber: number, el: HTMLDivElement | null) => {
        itemRefs.current[roiNumber] = el;
    };

    const titleIconSx = (active: boolean) => ({
        width: ICON_BUTTON_PX,
        height: ICON_BUTTON_PX,
        color: active ? 'primary.main' : 'text.secondary',
    });

    return (
        <Box
            position="relative"
            display="grid"
            gridTemplateRows={'auto 1fr auto'}
            gridTemplateColumns="minmax(0, 1fr)"
        >
            <Box
                display="flex"
                flexDirection="column"
                gap={0.5}
                px={2}
                pt={1}
                pb={0.5}
            >
                <Box display="flex" alignItems="center" gap="2px" height={28}>
                    <SectionTitle
                        id="structure.countLabel"
                        count={filteredRois.length}
                        countTestId="structure-count"
                    />
                    <Tooltip
                        title={
                            <FormattedMessage id="structure.showOrHideAll" />
                        }
                    >
                        <IconButton
                            onClick={handleToggleAllVisibility}
                            aria-label={
                                allVisible
                                    ? 'toggle-all-visibility-eye'
                                    : 'toggle-all-visibility-eyeoff'
                            }
                            data-testid="toggle-all-visibility"
                            sx={titleIconSx(false)}
                        >
                            {allVisible ? (
                                <VisibilityOutlined
                                    sx={{ fontSize: ICON_PX }}
                                />
                            ) : (
                                <VisibilityOffOutlined
                                    sx={{ fontSize: ICON_PX }}
                                />
                            )}
                        </IconButton>
                    </Tooltip>
                    {!visibilityOnly && (
                        <>
                            {/* Multi-select, next to the filter: ctrl-click
                                reaches the same selection but announces itself
                                nowhere, so a user who does not already know the
                                shortcut cannot find the bulk actions. */}
                            <Tooltip
                                title={
                                    <FormattedMessage
                                        id={
                                            multiSelectActive
                                                ? 'structure.multiSelect.exit'
                                                : 'structure.multiSelect.enter'
                                        }
                                    />
                                }
                            >
                                <span>
                                    <IconButton
                                        onClick={toggleMultiSelectMode}
                                        disabled={
                                            structureSet.roiArray.length < 2
                                        }
                                        aria-pressed={multiSelectActive}
                                        aria-label={intl.formatMessage({
                                            id: multiSelectActive
                                                ? 'structure.multiSelect.exit'
                                                : 'structure.multiSelect.enter',
                                        })}
                                        data-testid="multi-select-toggle"
                                        sx={titleIconSx(multiSelectActive)}
                                    >
                                        <Checklist sx={{ fontSize: ICON_PX }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {!readOnly && !visibilityOnly && (
                        <>
                            <Divider
                                orientation="vertical"
                                flexItem
                                sx={{ my: '6px', mx: 0.5 }}
                            />
                            <Box ref={createButtonRef}>
                                <Tooltip
                                    title={
                                        <FormattedMessage id="structure.newStructure" />
                                    }
                                >
                                    <span>
                                        <IconButton
                                            onClick={() =>
                                                setCreateMenuOpen(
                                                    (prev) => !prev
                                                )
                                            }
                                            disabled={structureSet.isApproved}
                                            aria-label={intl.formatMessage({
                                                id: 'structure.newStructure',
                                            })}
                                            aria-controls={
                                                createMenuOpen
                                                    ? 'create-menu'
                                                    : undefined
                                            }
                                            aria-haspopup="menu"
                                            data-testid="create-roi-button"
                                            sx={{
                                                // Over the filter button in the
                                                // search row, on the same centre.
                                                width: SEARCH_FIELD_PX,
                                                height: SEARCH_FIELD_PX,
                                                my: -0.5,
                                                color: 'primary.main',
                                            }}
                                        >
                                            <Add
                                                sx={{ fontSize: ADD_ICON_PX }}
                                            />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Popper
                                    sx={{ zIndex: 1 }}
                                    open={createMenuOpen}
                                    anchorEl={createButtonRef.current}
                                    role={undefined}
                                    transition
                                    disablePortal
                                    placement="bottom-end"
                                >
                                    {({ TransitionProps, placement }) => (
                                        <Grow
                                            {...TransitionProps}
                                            style={{
                                                transformOrigin:
                                                    placement === 'bottom-end'
                                                        ? 'right top'
                                                        : 'right bottom',
                                            }}
                                        >
                                            <Paper
                                                elevation={4}
                                                sx={{
                                                    mt: 0.5,
                                                    border: 1,
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <ClickAwayListener
                                                    onClickAway={
                                                        handleCloseCreateMenu
                                                    }
                                                >
                                                    <MenuList
                                                        id="create-menu"
                                                        autoFocusItem
                                                        dense
                                                    >
                                                        <MenuItem
                                                            onClick={() => {
                                                                createRoi();
                                                                setCreateMenuOpen(
                                                                    false
                                                                );
                                                            }}
                                                            disabled={
                                                                structureSet.isApproved
                                                            }
                                                        >
                                                            Structure
                                                        </MenuItem>
                                                        <MenuItem
                                                            onClick={() => {
                                                                createRoi(
                                                                    RoiInterpretedType.MARKER
                                                                );
                                                                setCreateMenuOpen(
                                                                    false
                                                                );
                                                            }}
                                                            disabled={
                                                                structureSet.isApproved
                                                            }
                                                        >
                                                            Point
                                                        </MenuItem>
                                                    </MenuList>
                                                </ClickAwayListener>
                                            </Paper>
                                        </Grow>
                                    )}
                                </Popper>
                            </Box>
                        </>
                    )}
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder={intl.formatMessage({
                            id: 'structure.searchPlaceholder',
                        })}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 18 }} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => setSearchQuery('')}
                                        aria-label={intl.formatMessage({
                                            id: 'patientList.search.clear',
                                        })}
                                        sx={{ width: 24, height: 24 }}
                                    >
                                        <Close sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{
                            // The rows below share this inset and icon column.
                            '& .MuiInputBase-root': {
                                height: SEARCH_FIELD_PX,
                                pl: 1,
                                fontSize: 13,
                            },
                            '& .MuiInputBase-input': { pl: 0 },
                        }}
                    />
                    <Tooltip
                        title={<FormattedMessage id="toggleTypeFilters" />}
                    >
                        <IconButton
                            onClick={() => setFilterPanelOpen((prev) => !prev)}
                            aria-label={intl.formatMessage({
                                id: 'toggleTypeFilters',
                            })}
                            aria-pressed={filterPanelOpen}
                            sx={{
                                ...titleIconSx(
                                    filterPanelOpen || selectedTypes.size > 0
                                ),
                                // As tall as the search field it sits beside.
                                width: SEARCH_FIELD_PX,
                                height: SEARCH_FIELD_PX,
                            }}
                        >
                            <FilterList sx={{ fontSize: ICON_PX }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Collapse
                    in={filterPanelOpen && presentTypes.length > 1}
                    unmountOnExit
                >
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        {presentTypes.map((type) => (
                            <Chip
                                key={type}
                                label={type}
                                variant="tag"
                                color={
                                    selectedTypes.has(type)
                                        ? 'primary'
                                        : 'default'
                                }
                                aria-pressed={selectedTypes.has(type)}
                                onClick={() => handleToggleType(type)}
                            />
                        ))}
                    </Box>
                </Collapse>
            </Box>
            <Box position="relative" height="100%">
                <Box position="absolute" width="100%" height="100%">
                    <List
                        ref={listRef}
                        data-structure-list
                        role="listbox"
                        aria-multiselectable="true"
                        aria-label={intl.formatMessage({
                            id: 'structure.bulk.listAriaLabel',
                        })}
                        sx={{
                            position: 'static',
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            height: '100%',
                            py: 0,
                            px: 2,
                            '& ul': { padding: 0 },
                        }}
                    >
                        {filteredRois.length === 0 && sortedRois.length > 0 && (
                            <Typography
                                component="li"
                                fontSize={12}
                                color="text.secondary"
                                px={0.5}
                                data-testid="no-structure-match"
                            >
                                <FormattedMessage id="structure.noSearchMatch" />
                            </Typography>
                        )}
                        {filteredRois.map((roi) => (
                            <div
                                key={`${structureSet.dicomUid}-${roi.roiNumber}`}
                                ref={(el) => assignItemRef(roi.roiNumber, el)}
                            >
                                <StructureLayer
                                    roi={roi}
                                    roiId={roiIdByNumber.get(roi.roiNumber)}
                                    isMultiSelected={
                                        !visibilityOnly &&
                                        multiSelected.has(roi.roiNumber)
                                    }
                                    showSelectionCheckbox={
                                        !visibilityOnly && multiSelectMode
                                    }
                                    onSelectionInput={
                                        visibilityOnly
                                            ? undefined
                                            : handleRowSelectionInput
                                    }
                                    viewOnly={visibilityOnly}
                                />
                            </div>
                        ))}
                    </List>
                </Box>
            </Box>
            {visibilityOnly ? null : multiSelectMode ||
              multiSelected.size >= 2 ? (
                <BulkActionsPanel
                    structureSet={structureSet}
                    selectedRoiNumbers={multiSelected}
                    hiddenSelectedRoiNumbers={hiddenSelectedRoiNumbers}
                    onClearSelection={clearMultiSelection}
                    onRemoveFromSelection={handleRemoveFromSelection}
                    readOnly={readOnly}
                />
            ) : selectedStructure ? (
                <StructureDetails
                    roi={selectedStructure}
                    roiId={roiIdByNumber.get(selectedStructure.roiNumber)}
                >
                    {renderExtraDetails?.(
                        selectedStructure,
                        roiIdByNumber.get(selectedStructure.roiNumber)
                    )}
                </StructureDetails>
            ) : selectHint && structureSet.roiArray.length > 0 ? (
                <DetailsCardHint data-testid="structure-select-hint">
                    {selectHint}
                </DetailsCardHint>
            ) : null}
        </Box>
    );
}
